import React, { useEffect, useMemo, useState } from "react";
import { Flex, Text, TextField, Button, Select, Dialog, IconButton, Tabs } from "@radix-ui/themes";
import { ArrowRightIcon } from "@radix-ui/react-icons";
import { ActiveIntegrateCard, PermissionAlertDialog, ToastAlert } from "components";
import { useProfileContext, useLocationContext } from "context";
import {
  useGetAllBrands,
  useValidateDotdigital,
  useSaveDotdigitalConfigs,
  useGetDotdigitalLists,
  useGetDotdigitalMappings,
  useSaveDotdigitalMappings,
  useDeactivateDotdigital,
} from "hooks";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "../../../context/ToastContext";
import { AuthDotdigitalDialog } from "./AuthDotdigitalDialog";
import {
  DOTDIGITAL_CUSTOM_FIELD_MAPPINGS,
  buildDefaultDotdigitalFieldMappings,
  normalizeDotdigitalFieldMappings,
} from "./dotdigitalFieldMappings";

const DOTDIGITAL_REGION_OPTIONS = [
  "https://r1-app.dotdigital.com/",
  "https://r2-app.dotdigital.com/",
  "https://r3-app.dotdigital.com/",
];

const DOTDIGITAL_EMAIL_OPT_IN_OPTIONS = [
  { value: "single", label: "Single opt-in" },
  { value: "verifiedDouble", label: "Verified double opt-in" },
];

const DOTDIGITAL_CONTACT_SYNC_AUDIENCE_OPTIONS = [
  { value: "subscribed", label: "Only opted-in guests" },
  { value: "all", label: "All guests whether they opted-in or not" },
];

function toStringValue(value) {
  if (value === null || typeof value === "undefined") {
    return "";
  }

  return String(value).trim();
}

function normalizeRegion(region) {
  return toStringValue(region) || DOTDIGITAL_REGION_OPTIONS[0];
}

function normalizeEmailOptInType(value) {
  return DOTDIGITAL_EMAIL_OPT_IN_OPTIONS.some((option) => option.value === value) ? value : "single";
}

function normalizeSyncOnlySubscribedContacts(value) {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "string") {
    return value !== "all" && !["false", "0", "no", "off"].includes(value.trim().toLowerCase());
  }

  return true;
}

function normalizeDotdigitalLists(lists) {
  return (Array.isArray(lists) ? lists : [])
    .map((list) => ({
      id: toStringValue(list?.id ?? list?.list_id),
      name: toStringValue(list?.name ?? list?.list_name),
    }))
    .filter((list) => list.id || list.name);
}

function getRegionCode(region) {
  const regionValue = toStringValue(region);
  const match = /(?:https?:\/\/)?(r\d+)-app\.dotdigital\.com\/?/i.exec(regionValue);

  if (match?.[1]) {
    return match[1].toUpperCase();
  }

  return regionValue.replace(/^https?:\/\//, "").replace(/\/$/, "") || "Region";
}

function buildDefaultFormData() {
  return {
    apiUser: "",
    apiPassword: "",
    accountRegion: DOTDIGITAL_REGION_OPTIONS[0],
    emailOptInType: "single",
    syncOnlySubscribedContacts: true,
    fieldMappings: buildDefaultDotdigitalFieldMappings(),
  };
}

function buildDotdigitalConfigId(rawConfig, fallbackIndex = 0) {
  const explicitId = rawConfig?.id ?? rawConfig?.dotdigital_config_id ?? rawConfig?.config_id ?? rawConfig?.persistedId ?? null;

  if (explicitId !== null && typeof explicitId !== "undefined" && String(explicitId).trim()) {
    return String(explicitId).trim();
  }

  const apiUser = toStringValue(rawConfig?.api_user ?? rawConfig?.apiUser);
  const accountRegion = normalizeRegion(rawConfig?.account_region ?? rawConfig?.accountRegion);

  if (apiUser || accountRegion) {
    return `draft:${apiUser}::${accountRegion}`;
  }

  return `draft:config-${fallbackIndex + 1}`;
}

function normalizeDotdigitalConfigs(rawConfigs = [], fallbackConfig = null, fallbackLists = []) {
  const sourceConfigs = Array.isArray(rawConfigs) && rawConfigs.length > 0
    ? rawConfigs
    : fallbackConfig
      ? [{ ...fallbackConfig, lists: fallbackLists }]
      : [];

  return sourceConfigs
    .map((rawConfig, index) => {
      const id = buildDotdigitalConfigId(rawConfig, index);
      const persistedId = rawConfig?.persistedId ?? rawConfig?.id ?? rawConfig?.dotdigital_config_id ?? rawConfig?.config_id ?? null;
      const apiUser = toStringValue(rawConfig?.api_user ?? rawConfig?.apiUser);
      const accountRegion = normalizeRegion(rawConfig?.account_region ?? rawConfig?.accountRegion);
      const emailOptInType = normalizeEmailOptInType(rawConfig?.email_opt_in_type ?? rawConfig?.emailOptInType);
      const syncOnlySubscribedContacts = normalizeSyncOnlySubscribedContacts(rawConfig?.sync_only_subscribed_contacts ?? rawConfig?.syncOnlySubscribedContacts);
      const fieldMappings = normalizeDotdigitalFieldMappings(rawConfig?.field_mappings ?? rawConfig?.fieldMappings);
      const lists = normalizeDotdigitalLists(rawConfig?.lists ?? rawConfig?.address_books ?? (sourceConfigs.length === 1 ? fallbackLists : []));

      return {
        id,
        persistedId: persistedId === null || typeof persistedId === "undefined" ? null : String(persistedId),
        apiUser,
        accountRegion,
        emailOptInType,
        syncOnlySubscribedContacts,
        fieldMappings,
        lists,
        localId: rawConfig?.localId || `${apiUser.toLowerCase()}::${accountRegion}`,
        label: toStringValue(rawConfig?.label ?? rawConfig?.name)
          || [apiUser || `Account ${index + 1}`, getRegionCode(accountRegion)].filter(Boolean).join(" • "),
      };
    })
    .filter((config) => config.apiUser || config.lists.length > 0);
}

function mergeDotdigitalConfigCollections(...collections) {
  const merged = new Map();

  collections.forEach((collection) => {
    (Array.isArray(collection) ? collection : []).forEach((rawConfig, index) => {
      const key = buildDotdigitalConfigId(rawConfig, merged.size + index);
      const existing = merged.get(key) || {};
      const incomingLists = normalizeDotdigitalLists(rawConfig?.lists ?? rawConfig?.address_books);

      merged.set(key, {
        ...existing,
        ...rawConfig,
        lists: incomingLists.length > 0 ? incomingLists : existing.lists || [],
      });
    });
  });

  return Array.from(merged.values());
}

function getDotdigitalConfigLabel(config) {
  if (!config) {
    return "Select a Dotdigital account";
  }

  return config.label || [config.apiUser, getRegionCode(config.accountRegion)].filter(Boolean).join(" • ");
}

function getDotdigitalConfigLists(configs, configId) {
  if (!configId) {
    return [];
  }

  return (configs || []).find((config) => String(config.id) === String(configId))?.lists || [];
}

function getDefaultDotdigitalConfigId(configs) {
  return Array.isArray(configs) && configs.length === 1 ? configs[0].id : "";
}

function buildValidatedDotdigitalConfig(config, configId, lists) {
  const persistedId = toStringValue(configId);
  const apiUser = toStringValue(config?.apiUser);
  const accountRegion = normalizeRegion(config?.accountRegion);

  return {
    id: persistedId || buildDotdigitalConfigId(config),
    persistedId: persistedId || null,
    localId: config?.localId || `${apiUser.toLowerCase()}::${accountRegion}`,
    apiUser,
    apiPassword: config?.apiPassword || "",
    accountRegion,
    emailOptInType: normalizeEmailOptInType(config?.emailOptInType ?? config?.email_opt_in_type),
    syncOnlySubscribedContacts: normalizeSyncOnlySubscribedContacts(config?.syncOnlySubscribedContacts ?? config?.sync_only_subscribed_contacts),
    fieldMappings: normalizeDotdigitalFieldMappings(config?.fieldMappings ?? config?.field_mappings),
    lists: normalizeDotdigitalLists(lists),
    label: [apiUser || "Account", getRegionCode(accountRegion)].filter(Boolean).join(" • "),
  };
}

function buildDotdigitalConfigSavePayload(config) {
  return {
    local_id: config?.localId,
    api_user: toStringValue(config?.apiUser),
    api_user_password: config?.apiPassword || "",
    account_region: normalizeRegion(config?.accountRegion),
    email_opt_in_type: normalizeEmailOptInType(config?.emailOptInType),
    sync_only_subscribed_contacts: normalizeSyncOnlySubscribedContacts(config?.syncOnlySubscribedContacts),
    field_mappings: normalizeDotdigitalFieldMappings(config?.fieldMappings),
    config_id: config?.persistedId || undefined,
  };
}

function getDotdigitalApiErrorMessage(error, fallbackMessage) {
  const backendMessage = error?.response?.data?.message;

  if (backendMessage) {
    return backendMessage;
  }

  return fallbackMessage;
}

export function ActiveDotdigitalIntegration() {
  const { userProfile } = useProfileContext();
  const { initialItems, locationsData } = useLocationContext();
  const { setToastMessage } = useToast();
  const queryClient = useQueryClient();
  const { data: brandsData, isLoading: isBrandsLoading } = useGetAllBrands(userProfile?.organisation_id);
  const { data: dotdigitalMappingsData, isLoading: isMappingsLoading } = useGetDotdigitalMappings(userProfile?.organisation_id);
  const { data: dotdigitalListsData, isLoading: isListsLoading } = useGetDotdigitalLists(userProfile?.organisation_id);
  const validateDotdigital = useValidateDotdigital();
  const saveDotdigitalConfigs = useSaveDotdigitalConfigs();
  const saveDotdigitalMappings = useSaveDotdigitalMappings();
  const deactivateDotdigital = useDeactivateDotdigital();

  const [formData, setFormData] = useState(buildDefaultFormData);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPermissionAlert, setShowPermissionAlert] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isSavingCredentials, setIsSavingCredentials] = useState(false);
  const [credentialsApiError, setCredentialsApiError] = useState("");
  const [openAddAccountDialog, setOpenAddAccountDialog] = useState(false);
  const [addAccountFormData, setAddAccountFormData] = useState(buildDefaultFormData);
  const [addAccountFieldErrors, setAddAccountFieldErrors] = useState({});
  const [pendingDotdigitalConfigs, setPendingDotdigitalConfigs] = useState([]);
  const [addAccountApiError, setAddAccountApiError] = useState("");
  const [isValidatingPendingAccount, setIsValidatingPendingAccount] = useState(false);
  const [isAddingAccounts, setIsAddingAccounts] = useState(false);
  const [isBrandsSaving, setIsBrandsSaving] = useState(false);
  const [isIndependentSaving, setIsIndependentSaving] = useState(false);
  const [isSavingFieldMappings, setIsSavingFieldMappings] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [dotdigitalConfigs, setDotdigitalConfigs] = useState([]);
  const [selectedConfigId, setSelectedConfigId] = useState("");
  const [fieldMappings, setFieldMappings] = useState(buildDefaultDotdigitalFieldMappings);
  const [originalFieldMappings, setOriginalFieldMappings] = useState(buildDefaultDotdigitalFieldMappings);
  const [bconnectBrands, setBconnectBrands] = useState([]);
  const [independentLocations, setIndependentLocations] = useState([]);
  const [locationMappings, setLocationMappings] = useState({});
  const [originalLocationMappings, setOriginalLocationMappings] = useState({});
  const [brandSelections, setBrandSelections] = useState({});

  const isOwner = userProfile.user_role === "Owner";

  const resolveMappingConfigId = (mapping, configs) => {
    const explicitPersistedId = toStringValue(mapping?.dotdigital_config_id ?? mapping?.config_id);
    if (explicitPersistedId) {
      const matchedById = (configs || []).find(
        (config) => String(config.persistedId ?? config.id) === explicitPersistedId
      );
      if (matchedById) {
        return matchedById.id;
      }
    }

    return getDefaultDotdigitalConfigId(configs);
  };

  useEffect(() => {
    const mergedConfigs = mergeDotdigitalConfigCollections(
      dotdigitalListsData?.data?.configs,
      dotdigitalMappingsData?.data?.configs
    );
    const fallbackConfig = dotdigitalMappingsData?.data?.api_user
      ? {
          api_user: dotdigitalMappingsData?.data?.api_user,
          account_region: dotdigitalMappingsData?.data?.account_region,
        }
      : null;
    const normalizedConfigs = normalizeDotdigitalConfigs(
      mergedConfigs,
      fallbackConfig,
      dotdigitalListsData?.data?.lists || []
    );

    setDotdigitalConfigs(normalizedConfigs);
  }, [dotdigitalListsData, dotdigitalMappingsData]);

  useEffect(() => {
    if (dotdigitalConfigs.length === 0) {
      setSelectedConfigId("");
      return;
    }

    const hasSelectedConfig = dotdigitalConfigs.some((config) => String(config.id) === String(selectedConfigId));
    if (!hasSelectedConfig) {
      setSelectedConfigId(dotdigitalConfigs[0].id);
    }
  }, [dotdigitalConfigs, selectedConfigId]);

  const selectedConfig = useMemo(() => {
    if (dotdigitalConfigs.length === 0) {
      return null;
    }

    return dotdigitalConfigs.find((config) => String(config.id) === String(selectedConfigId)) || dotdigitalConfigs[0];
  }, [dotdigitalConfigs, selectedConfigId]);

  useEffect(() => {
    if (!selectedConfig) {
      return;
    }

    setFormData((previousFormData) => ({
      ...previousFormData,
      apiUser: selectedConfig.apiUser || "",
      apiPassword: "",
      accountRegion: selectedConfig.accountRegion || DOTDIGITAL_REGION_OPTIONS[0],
      emailOptInType: normalizeEmailOptInType(selectedConfig.emailOptInType),
      syncOnlySubscribedContacts: normalizeSyncOnlySubscribedContacts(selectedConfig.syncOnlySubscribedContacts),
    }));
    const selectedFieldMappings = normalizeDotdigitalFieldMappings(selectedConfig.fieldMappings);
    setFieldMappings(selectedFieldMappings);
    setOriginalFieldMappings(selectedFieldMappings);
  }, [selectedConfig]);

  useEffect(() => {
    const mappings = dotdigitalMappingsData?.data?.mappings?.location_mappings;
    if (!Array.isArray(mappings)) {
      return;
    }

    const serverMap = {};
    mappings.forEach((mapping) => {
      const key = String(mapping.bconnect_location_id);
      const configId = resolveMappingConfigId(mapping, dotdigitalConfigs);
      serverMap[key] = {
        dotdigital_config_id: configId || "",
        dotdigital_list_id: mapping.dotdigital_list_id || "",
        isMapped: Boolean(configId && mapping.dotdigital_list_id),
      };
    });

    setLocationMappings(serverMap);
    setOriginalLocationMappings(
      Object.fromEntries(
        Object.entries(serverMap).map(([key, value]) => [
          key,
          {
            dotdigital_config_id: value.dotdigital_config_id,
            dotdigital_list_id: value.dotdigital_list_id,
          },
        ])
      )
    );
  }, [dotdigitalMappingsData, dotdigitalConfigs]);

  useEffect(() => {
    if (brandsData && locationsData) {
      const defaultBrand = (brandsData || []).find((brand) => brand.is_default);
      const nonDefaultBrands = (brandsData || []).filter((brand) => !brand.is_default);
      setBconnectBrands(nonDefaultBrands || []);
      const defaultLocations = (locationsData || []).filter((loc) => loc.brand_id === defaultBrand?.id || loc.brand_id === null);
      setIndependentLocations(defaultLocations || []);
      return;
    }

    setBconnectBrands([]);
    setIndependentLocations([]);
  }, [brandsData, locationsData]);

  const brandLocationsMap = useMemo(() => {
    const map = {};
    if (!bconnectBrands) {
      return map;
    }

    bconnectBrands.forEach((brand) => {
      const sourceBrand = (brandsData || []).find((brandData) => String(brandData.id) === String(brand.id));
      if (sourceBrand && Array.isArray(sourceBrand.locations)) {
        map[brand.id] = sourceBrand.locations;
        return;
      }

      map[brand.id] = (locationsData || []).filter((loc) => String(loc.brand_id) === String(brand.id));
    });

    return map;
  }, [bconnectBrands, brandsData, locationsData]);

  const allBrandOwnedLocations = useMemo(() => Object.values(brandLocationsMap).flat(), [brandLocationsMap]);

  const checkHalfFilled = (locations) => {
    return (locations || []).some((loc) => {
      const locId = loc.id ?? loc.value ?? loc.location_id;
      const key = String(locId);
      const mapping = locationMappings[key] || { dotdigital_config_id: "", dotdigital_list_id: "" };
      const hasConfig = Boolean(mapping.dotdigital_config_id);
      const hasList = Boolean(mapping.dotdigital_list_id);

      return (hasConfig && !hasList) || (!hasConfig && hasList);
    });
  };

  const requireOwner = (callback) => {
    if (isOwner) {
      callback();
      return;
    }

    setShowPermissionAlert(true);
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData((previousFormData) => ({
      ...previousFormData,
      [fieldName]: value,
    }));

    setFieldErrors((previousErrors) => ({
      ...previousErrors,
      [fieldName]: false,
    }));
    setCredentialsApiError("");
  };

  const handleAddAccountFieldChange = (fieldName, value) => {
    setAddAccountFormData((previousFormData) => ({
      ...previousFormData,
      [fieldName]: value,
    }));

    setAddAccountFieldErrors((previousErrors) => ({
      ...previousErrors,
      [fieldName]: false,
    }));
    setAddAccountApiError("");
  };

  const handleAddAccountFieldMappingChange = (sourceField, value) => {
    setAddAccountFormData((previousFormData) => ({
      ...previousFormData,
      fieldMappings: {
        ...normalizeDotdigitalFieldMappings(previousFormData.fieldMappings),
        [sourceField]: value,
      },
    }));
    setAddAccountApiError("");
  };

  const resetAddAccountState = () => {
    setAddAccountFormData(buildDefaultFormData());
    setAddAccountFieldErrors({});
    setPendingDotdigitalConfigs([]);
    setAddAccountApiError("");
  };

  const handleAddAccountDialogOpenChange = (isOpen) => {
    setOpenAddAccountDialog(isOpen);
    if (!isOpen) {
      resetAddAccountState();
    }
  };

  const validatePendingAccount = async () => {
    const nextErrors = {
      apiUser: !toStringValue(addAccountFormData.apiUser),
      apiPassword: !toStringValue(addAccountFormData.apiPassword),
      accountRegion: !toStringValue(addAccountFormData.accountRegion),
    };

    if (Object.values(nextErrors).some(Boolean)) {
      setAddAccountFieldErrors(nextErrors);
      return null;
    }

    const nextConfig = {
      apiUser: toStringValue(addAccountFormData.apiUser),
      apiPassword: addAccountFormData.apiPassword || "",
      accountRegion: normalizeRegion(addAccountFormData.accountRegion),
      emailOptInType: normalizeEmailOptInType(addAccountFormData.emailOptInType),
      syncOnlySubscribedContacts: normalizeSyncOnlySubscribedContacts(addAccountFormData.syncOnlySubscribedContacts),
      fieldMappings: normalizeDotdigitalFieldMappings(addAccountFormData.fieldMappings),
    };

    setIsValidatingPendingAccount(true);
    setAddAccountApiError("");

    try {
      const result = await validateDotdigital.mutateAsync({
        api_user: nextConfig.apiUser,
        api_user_password: nextConfig.apiPassword,
        account_region: nextConfig.accountRegion,
        email_opt_in_type: nextConfig.emailOptInType,
        sync_only_subscribed_contacts: nextConfig.syncOnlySubscribedContacts,
        field_mappings: nextConfig.fieldMappings,
        organisation_id: userProfile.organisation_id,
        persist: false,
      });

      const validatedConfig = buildValidatedDotdigitalConfig(
        nextConfig,
        result?.data?.config_id || result?.data?.config?.id,
        result?.data?.lists || result?.data?.config?.lists || []
      );

      setPendingDotdigitalConfigs((previousConfigs) => {
        const remainingConfigs = (previousConfigs || []).filter((config) => config.localId !== validatedConfig.localId);
        return [...remainingConfigs, validatedConfig];
      });
      setAddAccountFormData(buildDefaultFormData());
      setAddAccountFieldErrors({});
      return validatedConfig;
    } catch (error) {
      const message = error?.response?.data?.message ?? "Failed to validate Dotdigital credentials. Please check and try again.";
      setAddAccountApiError(message);
      return null;
    } finally {
      setIsValidatingPendingAccount(false);
    }
  };

  const handleRemovePendingAccount = (localId) => {
    setPendingDotdigitalConfigs((previousConfigs) => (previousConfigs || []).filter((config) => config.localId !== localId));
    setAddAccountApiError("");
  };

  const handleAddAccounts = async () => {
    if (pendingDotdigitalConfigs.length === 0) {
      return;
    }

    setIsAddingAccounts(true);
    setAddAccountApiError("");

    try {
      const result = await saveDotdigitalConfigs.mutateAsync({
        organisation_id: userProfile.organisation_id,
        configs: pendingDotdigitalConfigs.map(buildDotdigitalConfigSavePayload),
      });

      const savedConfigsByLocalId = new Map(
        (result?.data?.configs || []).map((config, index) => [config.local_id || pendingDotdigitalConfigs[index]?.localId, config])
      );
      const savedConfigs = pendingDotdigitalConfigs.map((config) => {
        const savedConfig = savedConfigsByLocalId.get(config.localId);
        const persistedId = toStringValue(savedConfig?.id ?? savedConfig?.config_id ?? config.persistedId);

        if (!persistedId) {
          throw new Error("Failed to save one or more Dotdigital accounts.");
        }

        const validatedConfig = buildValidatedDotdigitalConfig(config, persistedId, config.lists);
        const { apiPassword, ...safeConfig } = validatedConfig;
        return safeConfig;
      });

      const mergedConfigs = normalizeDotdigitalConfigs(
        mergeDotdigitalConfigCollections(dotdigitalConfigs, savedConfigs)
      );
      setDotdigitalConfigs(mergedConfigs);
      if (savedConfigs.length > 0) {
        setSelectedConfigId(savedConfigs[savedConfigs.length - 1].id);
      }

      queryClient.invalidateQueries(["dotdigital-mappings", userProfile.organisation_id]);
      queryClient.invalidateQueries(["dotdigital-lists", userProfile.organisation_id]);
      queryClient.invalidateQueries(["dotdigital-status", userProfile.organisation_id]);
      setToastMessage("Dotdigital account(s) added successfully");
      handleAddAccountDialogOpenChange(false);
    } catch (error) {
      const message = error?.response?.data?.message ?? "Failed to save Dotdigital account(s). Please try again.";
      setAddAccountApiError(message);
    } finally {
      setIsAddingAccounts(false);
    }
  };

  const validateForm = () => {
    const isExistingConfig = Boolean(selectedConfig?.persistedId);
    const nextErrors = {
      apiUser: !formData.apiUser.trim(),
      apiPassword: !isExistingConfig && !formData.apiPassword,
      accountRegion: !formData.accountRegion,
    };

    setFieldErrors(nextErrors);
    return !Object.values(nextErrors).some(Boolean);
  };

  const handleSaveCredentials = () => {
    requireOwner(async () => {
      if (!validateForm()) {
        return;
      }

      setIsSavingCredentials(true);
      setCredentialsApiError("");
      try {
        const result = await validateDotdigital.mutateAsync({
          api_user: formData.apiUser.trim(),
          api_user_password: formData.apiPassword,
          account_region: formData.accountRegion,
          email_opt_in_type: normalizeEmailOptInType(formData.emailOptInType),
          sync_only_subscribed_contacts: normalizeSyncOnlySubscribedContacts(formData.syncOnlySubscribedContacts),
          organisation_id: userProfile.organisation_id,
          dotdigital_config_id: selectedConfig?.persistedId || undefined,
          config_id: selectedConfig?.persistedId || undefined,
        });
        if (result?.data?.status === "success") {
          const validatedConfig = buildValidatedDotdigitalConfig(
            {
              ...selectedConfig,
              apiUser: formData.apiUser.trim(),
              accountRegion: formData.accountRegion,
              emailOptInType: normalizeEmailOptInType(formData.emailOptInType),
              syncOnlySubscribedContacts: normalizeSyncOnlySubscribedContacts(formData.syncOnlySubscribedContacts),
            },
            result?.data?.config_id || result?.data?.config?.id || selectedConfig?.persistedId,
            result?.data?.lists || result?.data?.config?.lists || selectedConfig?.lists || []
          );
          setDotdigitalConfigs((previousConfigs) => normalizeDotdigitalConfigs(
            mergeDotdigitalConfigCollections(previousConfigs, [validatedConfig])
          ));
          setSelectedConfigId(validatedConfig.id);
          queryClient.invalidateQueries(["dotdigital-mappings", userProfile.organisation_id]);
          queryClient.invalidateQueries(["dotdigital-lists", userProfile.organisation_id]);
          queryClient.invalidateQueries(["dotdigital-status", userProfile.organisation_id]);
          setToastMessage("Credentials saved and validated successfully");
          setFormData((prev) => ({ ...prev, apiPassword: "" }));
        } else {
          const msg = result?.data?.message ?? "Failed to validate credentials";
          setCredentialsApiError(msg);
          setToastMessage(msg);
        }
      } catch (err) {
        const msg = getDotdigitalApiErrorMessage(err, "Failed to save credentials");
        setCredentialsApiError(msg);
        setToastMessage(msg);
      } finally {
        setIsSavingCredentials(false);
      }
    });
  };

  const handleLocationConfigChange = (bconnectLocationId, configId) => {
    const key = String(bconnectLocationId);

    setLocationMappings((previousMappings) => {
      const nextMappings = { ...(previousMappings || {}) };
      const existing = nextMappings[key] || { dotdigital_config_id: "", dotdigital_list_id: "", isMapped: false };
      const availableLists = getDotdigitalConfigLists(dotdigitalConfigs, configId);
      const dotdigitalListId = availableLists.some((list) => String(list.id) === String(existing.dotdigital_list_id))
        ? existing.dotdigital_list_id
        : "";

      nextMappings[key] = {
        ...existing,
        dotdigital_config_id: configId,
        dotdigital_list_id: dotdigitalListId,
        isMapped: Boolean(configId && dotdigitalListId),
      };
      return nextMappings;
    });
  };

  const handleLocationDotdigitalListChange = (bconnectLocationId, dotdigitalListId) => {
    const key = String(bconnectLocationId);
    setLocationMappings((previousMappings) => {
      const nextMappings = { ...(previousMappings || {}) };
      const existing = nextMappings[key] || { dotdigital_config_id: "", dotdigital_list_id: "", isMapped: false };
      nextMappings[key] = {
        ...existing,
        dotdigital_list_id: dotdigitalListId,
        isMapped: Boolean(existing.dotdigital_config_id && dotdigitalListId),
      };
      return nextMappings;
    });
  };

  const handleBrandConfigSelectionChange = (brandId, configId) => {
    setBrandSelections((previousSelections) => {
      const brandKey = String(brandId);
      const existing = previousSelections?.[brandKey] || { configId: "", listId: "" };
      const availableLists = getDotdigitalConfigLists(dotdigitalConfigs, configId);
      const listId = availableLists.some((list) => String(list.id) === String(existing.listId))
        ? existing.listId
        : "";

      return {
        ...(previousSelections || {}),
        [brandKey]: {
          configId,
          listId,
        },
      };
    });
  };

  const handleBrandListSelectionChange = (brandId, listId) => {
    setBrandSelections((previousSelections) => ({
      ...(previousSelections || {}),
      [String(brandId)]: {
        ...(previousSelections?.[String(brandId)] || { configId: "", listId: "" }),
        listId,
      },
    }));
  };

  const handleApplyBrandList = (brandId) => {
    const selectedConfig = brandSelections[String(brandId)]?.configId;
    const selectedList = brandSelections[String(brandId)]?.listId;

    if (!selectedConfig || !selectedList) {
      return;
    }

    const brandLocations = brandLocationsMap[brandId] || [];
    setLocationMappings((previousMappings) => {
      const nextMappings = { ...(previousMappings || {}) };
      brandLocations.forEach((loc) => {
        const locId = loc.id ?? loc.value ?? loc.location_id;
        const key = String(locId);
        const existing = nextMappings[key] || { dotdigital_config_id: "", dotdigital_list_id: "", isMapped: false };
        nextMappings[key] = {
          ...existing,
          dotdigital_config_id: selectedConfig,
          dotdigital_list_id: selectedList,
          isMapped: Boolean(selectedConfig && selectedList),
        };
      });
      return nextMappings;
    });
  };

  const hasBrandSectionChanges = useMemo(() => {
    return (allBrandOwnedLocations || []).some((loc) => {
      const locId = loc.id ?? loc.value ?? loc.location_id;
      const key = String(locId);
      const currentConfig = String(locationMappings[key]?.dotdigital_config_id || "");
      const originalConfig = String(originalLocationMappings[key]?.dotdigital_config_id || "");
      const currentList = String(locationMappings[key]?.dotdigital_list_id || "");
      const originalList = String(originalLocationMappings[key]?.dotdigital_list_id || "");
      return Boolean(currentConfig && currentList) && (currentConfig !== originalConfig || currentList !== originalList);
    });
  }, [allBrandOwnedLocations, locationMappings, originalLocationMappings]);

  const hasIndependentLocationChanges = useMemo(() => {
    return (independentLocations || []).some((loc) => {
      const locId = loc.id ?? loc.value ?? loc.location_id;
      const key = String(locId);
      const currentConfig = String(locationMappings[key]?.dotdigital_config_id || "");
      const originalConfig = String(originalLocationMappings[key]?.dotdigital_config_id || "");
      const currentList = String(locationMappings[key]?.dotdigital_list_id || "");
      const originalList = String(originalLocationMappings[key]?.dotdigital_list_id || "");
      return Boolean(currentConfig && currentList) && (currentConfig !== originalConfig || currentList !== originalList);
    });
  }, [independentLocations, locationMappings, originalLocationMappings]);

  const hasBrandHalfFilled = useMemo(() => checkHalfFilled(allBrandOwnedLocations), [allBrandOwnedLocations, locationMappings]);
  const hasIndependentHalfFilled = useMemo(() => checkHalfFilled(independentLocations), [independentLocations, locationMappings]);
  const hasFieldMappingChanges = useMemo(
    () => JSON.stringify(fieldMappings) !== JSON.stringify(originalFieldMappings),
    [fieldMappings, originalFieldMappings]
  );

  const handleFieldMappingChange = (sourceField, value) => {
    setFieldMappings((previousMappings) => ({
      ...previousMappings,
      [sourceField]: value,
    }));
  };

  const handleSaveFieldMappings = () => {
    requireOwner(async () => {
      if (!selectedConfig?.persistedId || !hasFieldMappingChanges) {
        return;
      }

      setIsSavingFieldMappings(true);
      try {
        await saveDotdigitalMappings.mutateAsync({
          organisation_id: userProfile.organisation_id,
          brand_mappings: [],
          location_mappings: [],
          field_mappings: {
            [selectedConfig.persistedId]: fieldMappings,
          },
        });

        setDotdigitalConfigs((previousConfigs) => (previousConfigs || []).map((config) => (
          String(config.id) === String(selectedConfig.id)
            ? { ...config, fieldMappings }
            : config
        )));
        setOriginalFieldMappings(fieldMappings);
        queryClient.invalidateQueries(["dotdigital-mappings", userProfile.organisation_id]);
        queryClient.invalidateQueries(["dotdigital-lists", userProfile.organisation_id]);
        setToastMessage("Data field mappings saved successfully");
      } catch (error) {
        console.error("Error saving Dotdigital field mappings.");
        setToastMessage("Failed to save data field mappings. Please try again.");
      } finally {
        setIsSavingFieldMappings(false);
      }
    });
  };

  const handleSaveLocationMappings = async (section) => {
    const savingSetters =
      section === "brands"
        ? [setIsBrandsSaving]
        : section === "independent"
          ? [setIsIndependentSaving]
          : [setIsBrandsSaving, setIsIndependentSaving];

    const selectedLocations =
      section === "brands"
        ? allBrandOwnedLocations || []
        : section === "independent"
          ? independentLocations || []
          : [...(independentLocations || []), ...(allBrandOwnedLocations || [])];

    const locationPayload = (selectedLocations || [])
      .map((loc) => {
        const locId = loc.id ?? loc.value ?? loc.location_id;
        const key = String(locId);
        const currentConfigId = locationMappings[key]?.dotdigital_config_id || null;
        const currentList = locationMappings[key]?.dotdigital_list_id || null;
        const originalConfigId = originalLocationMappings[key]?.dotdigital_config_id || null;
        const originalList = originalLocationMappings[key]?.dotdigital_list_id || null;

        if (!currentConfigId || !currentList) {
          return null;
        }

        if (String(currentConfigId) === String(originalConfigId) && String(currentList) === String(originalList)) {
          return null;
        }

        const dotdigitalConfig = dotdigitalConfigs.find((config) => String(config.id) === String(currentConfigId));
        const dotdigitalList = getDotdigitalConfigLists(dotdigitalConfigs, currentConfigId)
          .find((list) => String(list.id) === String(currentList));

        if (!dotdigitalConfig?.persistedId) {
          return null;
        }

        return {
          bconnect_location_id: locId,
          bconnect_location_name: loc.name ?? loc.label ?? "",
          dotdigital_config_id: dotdigitalConfig.persistedId,
          dotdigital_list_id: currentList,
          dotdigital_list_name: dotdigitalList?.name || "",
        };
      })
      .filter(Boolean);

    if (locationPayload.length === 0) {
      setToastMessage("No location changes to save");
      return;
    }

    savingSetters.forEach((setSaving) => setSaving(true));

    try {
      await saveDotdigitalMappings.mutateAsync({
        organisation_id: userProfile.organisation_id,
        brand_mappings: [],
        location_mappings: locationPayload,
      });
      queryClient.invalidateQueries(["dotdigital-mappings", userProfile.organisation_id]);
      queryClient.invalidateQueries(["dotdigital-lists", userProfile.organisation_id]);
      queryClient.invalidateQueries(["dotdigital-status"]);

      const nextOriginalMappings = { ...originalLocationMappings };
      locationPayload.forEach((mapping) => {
        nextOriginalMappings[String(mapping.bconnect_location_id)] = {
          dotdigital_config_id: mapping.dotdigital_config_id || "",
          dotdigital_list_id: mapping.dotdigital_list_id,
        };
      });
      setOriginalLocationMappings(nextOriginalMappings);
      setLocationMappings((previousMappings) => {
        const nextMappings = { ...(previousMappings || {}) };
        locationPayload.forEach((mapping) => {
          const key = String(mapping.bconnect_location_id);
          nextMappings[key] = {
            ...(nextMappings[key] || {}),
            dotdigital_config_id: mapping.dotdigital_config_id || "",
            dotdigital_list_id: mapping.dotdigital_list_id,
            isMapped: Boolean(mapping.dotdigital_config_id && mapping.dotdigital_list_id),
          };
        });
        return nextMappings;
      });
      setToastMessage("Location mappings saved successfully");
    } catch (error) {
      console.error("Error saving Dotdigital mappings:", error);
      setToastMessage(error?.response?.data?.message ?? "Failed to save location mappings");
    } finally {
      savingSetters.forEach((setSaving) => setSaving(false));
    }
  };

  const handleDisconnectClick = () => {
    requireOwner(() => {
      setShowConfirmDialog(true);
    });
  };

  const handleDisconnect = async () => {
    setIsDisconnecting(true);
    try {
      await deactivateDotdigital.mutateAsync({ organisation_id: userProfile.organisation_id });
      queryClient.invalidateQueries(["dotdigital-status"]);
      setToastMessage("Dotdigital integration disconnected.");
      setShowConfirmDialog(false);
    } catch (err) {
      const msg = err?.response?.data?.message ?? "Failed to disconnect Dotdigital integration";
      setToastMessage(msg);
    } finally {
      setIsDisconnecting(false);
    }
  };

  if (!initialItems) {
    return (
      <Flex direction="column" align="center" justify="center" style={{ height: "200px" }}>
        <Text>No locations available.</Text>
      </Flex>
    );
  }

  if (isBrandsLoading) {
    return (
      <Flex direction="column" align="center" justify="center" style={{ height: "200px" }}>
        <Text>Loading Dotdigital integration data...</Text>
      </Flex>
    );
  }

  if (isMappingsLoading || isListsLoading) {
    return (
      <Flex direction="column" align="center" justify="center" style={{ height: "200px" }}>
        <Text>Loading Dotdigital integration data...</Text>
      </Flex>
    );
  }

  return (
    <>
      <ActiveIntegrateCard.Root
        name="Dotdigital"
        subtitle="Map locations to Dotdigital accounts and lists"
        image_url="dotdigtal.png"
        option={{
          text: "Disconnect",
          color: "red",
          onClick: handleDisconnectClick,
        }}
      >
        <ActiveIntegrateCard.Section
          name="Accounts"
          subtitle="Review the connected Dotdigital account details. Mapping rows now select an account first, then a list from that account."
        >
          <Flex direction="column" gap="4" style={{ width: "100%" }}>
            <Flex justify="between" align="center">
              <Text size="2" weight="medium">
                Connected accounts
              </Text>
              <Button size="2" variant="soft" disabled={!isOwner} onClick={() => requireOwner(() => handleAddAccountDialogOpenChange(true))}>
                Add account
              </Button>
            </Flex>

            {dotdigitalConfigs.length > 1 ? (
              <Flex direction="column" gap="1" align="start">
                <Text size="2" weight="medium">
                  Selected account
                </Text>
                <Select.Root value={selectedConfigId || ""} onValueChange={setSelectedConfigId}>
                  <Select.Trigger style={{ width: "100%" }} />
                  <Select.Content position="popper">
                    {dotdigitalConfigs.map((config) => (
                      <Select.Item key={config.id} value={config.id}>
                        {getDotdigitalConfigLabel(config)}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
              </Flex>
            ) : null}

            <Tabs.Root defaultValue="sync" style={{ width: "100%" }}>
              <Tabs.List>
                <Tabs.Trigger value="sync">Sync settings</Tabs.Trigger>
                <Tabs.Trigger value="fields">Data fields</Tabs.Trigger>
              </Tabs.List>

              <Tabs.Content value="sync">
                <Flex direction="column" gap="3" pt="4" style={{ width: "100%" }}>
                  <Flex direction="column" gap="1" align="start">
                    <Text size="2" weight="medium">
                      API user
                    </Text>
                    <TextField.Root
                      value={formData.apiUser}
                      onChange={(event) => handleFieldChange("apiUser", event.target.value)}
                      placeholder="Enter your Dotdigital API user"
                      style={{ width: "100%" }}
                      disabled={!isOwner}
                    />
                    {fieldErrors.apiUser ? (
                      <Text size="1" color="red">
                        API user is required.
                      </Text>
                    ) : null}
                  </Flex>

                  <Flex direction="column" gap="1" align="start">
                    <Text size="2" weight="medium">
                      API user password
                    </Text>
                    <TextField.Root
                      type={showPassword ? "text" : "password"}
                      value={formData.apiPassword}
                      onChange={(event) => handleFieldChange("apiPassword", event.target.value)}
                      placeholder="Enter a new password to update"
                      style={{ width: "100%" }}
                      disabled={!isOwner}
                    />
                    {fieldErrors.apiPassword ? (
                      <Text size="1" color="red">
                        API user password is required.
                      </Text>
                    ) : null}
                  </Flex>

                  <Flex direction="column" gap="1" align="start">
                    <Text size="2" weight="medium">
                      Account region
                    </Text>
                    <Select.Root value={formData.accountRegion} onValueChange={(value) => handleFieldChange("accountRegion", value)} disabled={!isOwner}>
                      <Select.Trigger style={{ width: "100%" }} />
                      <Select.Content position="popper">
                        {DOTDIGITAL_REGION_OPTIONS.map((regionUrl) => (
                          <Select.Item key={regionUrl} value={regionUrl}>
                            {regionUrl}
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Root>
                    {fieldErrors.accountRegion ? (
                      <Text size="1" color="red">
                        Account region is required.
                      </Text>
                    ) : null}
                  </Flex>

                  <Flex direction="column" gap="1" align="start">
                    <Text size="2" weight="medium">
                      Email opt-in type
                    </Text>
                    <Select.Root
                      value={formData.emailOptInType || "single"}
                      onValueChange={(value) => handleFieldChange("emailOptInType", value)}
                      disabled={!isOwner}
                    >
                      <Select.Trigger style={{ width: "100%" }} />
                      <Select.Content position="popper">
                        {DOTDIGITAL_EMAIL_OPT_IN_OPTIONS.map((option) => (
                          <Select.Item key={option.value} value={option.value}>
                            {option.label}
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Root>
                  </Flex>

                  <Flex direction="column" gap="1" align="start">
                    <Text size="2" weight="medium">
                      Contact sync audience
                    </Text>
                    <Select.Root
                      value={formData.syncOnlySubscribedContacts === false ? "all" : "subscribed"}
                      onValueChange={(value) => handleFieldChange("syncOnlySubscribedContacts", value !== "all")}
                      disabled={!isOwner}
                    >
                      <Select.Trigger style={{ width: "100%" }} />
                      <Select.Content position="popper">
                        {DOTDIGITAL_CONTACT_SYNC_AUDIENCE_OPTIONS.map((option) => (
                          <Select.Item key={option.value} value={option.value}>
                            {option.label}
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Root>
                  </Flex>

                  {credentialsApiError ? (
                    <Flex
                      direction="column"
                      gap="1"
                      style={{
                        padding: "10px 12px",
                        borderRadius: "12px",
                        border: "1px solid var(--red-a5)",
                        background: "var(--red-2)",
                      }}
                    >
                      <Text size="1" weight="medium" color="red">
                        Unable to save changes
                      </Text>
                      <Text size="1" color="red">
                        {credentialsApiError}
                      </Text>
                    </Flex>
                  ) : null}

                  <Flex justify="end" gap="3">
                    <Button variant="soft" color="gray" onClick={() => setShowPassword((previous) => !previous)} disabled={!isOwner}>
                      {showPassword ? "Hide password" : "Show password"}
                    </Button>
                    <Button variant="soft" color="accent" onClick={handleSaveCredentials} loading={isSavingCredentials} disabled={!isOwner || isSavingCredentials}>
                      Save changes
                    </Button>
                  </Flex>
                </Flex>
              </Tabs.Content>

              <Tabs.Content value="fields">
                <Flex direction="column" gap="2" pt="3" style={{ width: "100%" }}>
                  <Flex direction="column" gap="1" mb="2">
                    <Text size="2" weight="medium">Data fields</Text>
                    <Text size="1" color="gray">
                      Map b connect fields to the matching Dotdigital data field names for this account.
                    </Text>
                  </Flex>

                  <Flex direction="row" justify="between" align="center" style={{ width: "100%" }}>
                    <Text size="2" weight="medium" style={{ width: "46%" }}>b connect</Text>
                    <Text size="2" weight="medium" style={{ width: "46%" }}>Dotdigital data field</Text>
                  </Flex>

                  {DOTDIGITAL_CUSTOM_FIELD_MAPPINGS.map((field) => (
                    <Flex key={field.source} direction="row" justify="between" align="center" style={{ width: "100%" }}>
                      <Flex style={{ width: "46%", minWidth: 0 }}>
                        <TextField.Root value={field.label} disabled style={{ width: "100%", backgroundColor: "var(--gray-3)" }} />
                      </Flex>
                      <Flex align="center" px="2">
                        <IconButton size="2" variant="ghost" color="gray" style={{ pointerEvents: "none" }}>
                          <ArrowRightIcon width="16" height="16" />
                        </IconButton>
                      </Flex>
                      <Flex style={{ width: "46%", minWidth: 0 }}>
                        <TextField.Root
                          value={fieldMappings[field.source] || ""}
                          onChange={(event) => handleFieldMappingChange(field.source, event.target.value)}
                          placeholder={field.defaultDotdigitalField}
                          style={{ width: "100%" }}
                          disabled={!isOwner}
                        />
                      </Flex>
                    </Flex>
                  ))}

                  <Flex justify="end">
                    <Button
                      variant="soft"
                      color="accent"
                      onClick={handleSaveFieldMappings}
                      loading={isSavingFieldMappings}
                      disabled={!isOwner || !selectedConfig?.persistedId || !hasFieldMappingChanges || isSavingFieldMappings}
                    >
                      Save data fields
                    </Button>
                  </Flex>
                </Flex>
              </Tabs.Content>
            </Tabs.Root>
          </Flex>
        </ActiveIntegrateCard.Section>

        <ActiveIntegrateCard.Section name="Brands" subtitle="Group: b connect brands with each of their locations">
          <Flex direction="column" gap="4" style={{ width: "100%" }}>
            <Flex direction="row" justify="between" style={{ width: "100%" }}>
              <Text size="2" weight="medium" style={{ width: "32%" }}>b connect location</Text>
              <Text size="2" weight="medium" style={{ width: "32%" }}>Dotdigital account</Text>
              <Text size="2" weight="medium" style={{ width: "32%" }}>Dotdigital list</Text>
            </Flex>

            {(bconnectBrands || []).length > 0 ? (
              (bconnectBrands || []).map((brand) => {
                const brandLocations = brandLocationsMap[brand.id] || [];
                const brandSelection = brandSelections[String(brand.id)] || { configId: "", listId: "" };
                const brandLists = getDotdigitalConfigLists(dotdigitalConfigs, brandSelection.configId);

                return (
                  <Flex key={brand.id} direction="column" gap="2" style={{ width: "100%" }}>
                    <Flex direction="row" style={{ width: "100%", alignItems: "center", gap: 8 }}>
                      <Text size="2" weight="medium" style={{ flex: "1 1 0%", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        [{brand.name}]
                      </Text>

                      {brandLocations.length > 0 ? (
                        <Flex style={{ marginLeft: "8px", gap: 8, alignItems: "center", flexShrink: 0 }}>
                          <Select.Root
                            value={brandSelection.configId || ""}
                            onValueChange={(value) => handleBrandConfigSelectionChange(brand.id, value)}
                            disabled={!isOwner}
                          >
                            <Select.Trigger placeholder="Apply account..." style={{ width: "220px", boxSizing: "border-box" }}>
                              <div style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%" }} title={brandSelection.configId ? (getDotdigitalConfigLabel(dotdigitalConfigs.find((config) => String(config.id) === String(brandSelection.configId))) || "") : "Apply account..."}>
                                {brandSelection.configId ? (getDotdigitalConfigLabel(dotdigitalConfigs.find((config) => String(config.id) === String(brandSelection.configId))) || "") : "Apply account..."}
                              </div>
                            </Select.Trigger>
                            <Select.Content side="bottom" align="start" sideOffset={4}>
                              {dotdigitalConfigs.map((config) => (
                                <Select.Item key={config.id} value={config.id} title={getDotdigitalConfigLabel(config)}>
                                  {getDotdigitalConfigLabel(config)}
                                </Select.Item>
                              ))}
                            </Select.Content>
                          </Select.Root>

                          <Select.Root
                            value={brandSelection.listId || ""}
                            onValueChange={(value) => handleBrandListSelectionChange(brand.id, value)}
                            disabled={!isOwner || !brandSelection.configId}
                          >
                            <Select.Trigger placeholder="Apply list..." style={{ width: "220px", boxSizing: "border-box" }}>
                              <div style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%" }} title={brandSelection.listId ? (brandLists.find((list) => String(list.id) === String(brandSelection.listId))?.name || "") : (brandSelection.configId ? "Apply list..." : "Choose an account first") }>
                                {brandSelection.listId
                                  ? (brandLists.find((list) => String(list.id) === String(brandSelection.listId))?.name || "")
                                  : (brandSelection.configId ? "Apply list..." : "Choose an account first")}
                              </div>
                            </Select.Trigger>
                            <Select.Content side="bottom" align="start" sideOffset={4}>
                              {brandLists.map((list) => (
                                <Select.Item key={list.id} value={list.id} title={list.name}>
                                  {list.name}
                                </Select.Item>
                              ))}
                            </Select.Content>
                          </Select.Root>

                          <Button size="1" variant="soft" onClick={() => handleApplyBrandList(brand.id)} disabled={!isOwner || !brandSelection.configId || !brandSelection.listId} style={{ flexShrink: 0 }}>
                            Apply
                          </Button>
                        </Flex>
                      ) : null}
                    </Flex>

                    {brandLocations.length > 0 ? (
                      brandLocations.map((loc) => {
                        const locId = loc.id ?? loc.value ?? loc.location_id;
                        const key = String(locId);
                        const mapping = locationMappings[key] || {};
                        const availableLists = getDotdigitalConfigLists(dotdigitalConfigs, mapping.dotdigital_config_id);
                        const selectedConfigLabel = mapping.dotdigital_config_id
                          ? getDotdigitalConfigLabel(dotdigitalConfigs.find((config) => String(config.id) === String(mapping.dotdigital_config_id)))
                          : "Select a Dotdigital account";
                        const selectedListName = mapping.dotdigital_list_id
                          ? availableLists.find((list) => String(list.id) === String(mapping.dotdigital_list_id))?.name || ""
                          : (mapping.dotdigital_config_id ? "Select a Dotdigital list" : "Choose an account first");

                        return (
                          <Flex key={key} direction="row" justify="between" style={{ width: "100%" }}>
                            <Flex style={{ width: "32%", minWidth: 0 }}>
                              <Flex style={{ width: "100%", backgroundColor: "var(--gray-3)", border: "1px solid var(--gray-a7)", borderRadius: "var(--radius-2)", padding: "0 var(--space-2)", height: "34px", alignItems: "center", overflow: "hidden" }}>
                                <Text size="2" title={loc.name ?? loc.label ?? ""} style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%", color: "var(--gray-a11)" }}>
                                  {loc.name ?? loc.label ?? ""}
                                </Text>
                              </Flex>
                            </Flex>

                            <Flex align="center" style={{ paddingLeft: "8px", paddingRight: "8px" }}>
                              <IconButton size="2" variant="ghost" color="gray" style={{ pointerEvents: "none" }}>
                                <ArrowRightIcon width="16" height="16" />
                              </IconButton>
                            </Flex>

                            <Flex style={{ width: "32%", minWidth: 0 }}>
                              <Select.Root value={mapping.dotdigital_config_id || ""} onValueChange={(value) => handleLocationConfigChange(key, value)} disabled={!isOwner}>
                                <Select.Trigger placeholder="Select account..." style={{ width: "100%", boxSizing: "border-box" }}>
                                  <div style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%" }} title={selectedConfigLabel}>
                                    {selectedConfigLabel}
                                  </div>
                                </Select.Trigger>
                                <Select.Content side="bottom" align="start" sideOffset={4}>
                                  {dotdigitalConfigs.map((config) => (
                                    <Select.Item key={config.id} value={config.id} title={getDotdigitalConfigLabel(config)}>
                                      {getDotdigitalConfigLabel(config)}
                                    </Select.Item>
                                  ))}
                                </Select.Content>
                              </Select.Root>
                            </Flex>

                            <Flex align="center" style={{ paddingLeft: "8px", paddingRight: "8px" }}>
                              <IconButton size="2" variant="ghost" color="gray" style={{ pointerEvents: "none" }}>
                                <ArrowRightIcon width="16" height="16" />
                              </IconButton>
                            </Flex>

                            <Flex style={{ width: "32%", minWidth: 0 }}>
                              <Select.Root value={mapping.dotdigital_list_id || ""} onValueChange={(value) => handleLocationDotdigitalListChange(key, value)} disabled={!isOwner || !mapping.dotdigital_config_id}>
                                <Select.Trigger placeholder="Select list..." style={{ width: "100%", boxSizing: "border-box" }}>
                                  <div style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%" }} title={selectedListName}>
                                    {selectedListName}
                                  </div>
                                </Select.Trigger>
                                <Select.Content side="bottom" align="start" sideOffset={4}>
                                  {availableLists.map((list) => <Select.Item key={list.id} value={list.id} title={list.name}>{list.name}</Select.Item>)}
                                </Select.Content>
                              </Select.Root>
                            </Flex>
                          </Flex>
                        );
                      })
                    ) : (
                      <Flex justify="center" style={{ padding: "8px 0 16px 0" }}>
                        <Text size="2" style={{ color: "var(--gray-a9)" }}>No locations for this brand</Text>
                      </Flex>
                    )}
                  </Flex>
                );
              })
            ) : (
              <Flex justify="center" style={{ padding: "20px 0" }}>
                <Text>No brands found.</Text>
              </Flex>
            )}

            <Flex justify="end">
              <Button size="2" variant="solid" color="blue" onClick={() => handleSaveLocationMappings("brands")} loading={isBrandsSaving} disabled={!isOwner || !hasBrandSectionChanges || hasBrandHalfFilled || isBrandsSaving || isIndependentSaving}>
                Save changes
              </Button>
            </Flex>
          </Flex>
        </ActiveIntegrateCard.Section>

        <ActiveIntegrateCard.Section name="Locations" subtitle="Map independent b connect locations to a Dotdigital account and list">
          <Flex direction="column" gap="4" style={{ width: "100%" }}>
            <Flex direction="row" justify="between" style={{ width: "100%" }}>
              <Text size="2" weight="medium" style={{ width: "32%" }}>b connect location</Text>
              <Text size="2" weight="medium" style={{ width: "32%" }}>Dotdigital account</Text>
              <Text size="2" weight="medium" style={{ width: "32%" }}>Dotdigital list</Text>
            </Flex>

            {(independentLocations || []).length > 0 ? (
              independentLocations.map((loc) => {
                const locId = loc.id ?? loc.value ?? loc.location_id;
                const key = String(locId);
                const mapping = locationMappings[key] || {};
                const availableLists = getDotdigitalConfigLists(dotdigitalConfigs, mapping.dotdigital_config_id);
                const selectedConfigLabel = mapping.dotdigital_config_id
                  ? getDotdigitalConfigLabel(dotdigitalConfigs.find((config) => String(config.id) === String(mapping.dotdigital_config_id)))
                  : "Select a Dotdigital account";
                const selectedListName = mapping.dotdigital_list_id
                  ? availableLists.find((list) => String(list.id) === String(mapping.dotdigital_list_id))?.name || ""
                  : (mapping.dotdigital_config_id ? "Select a Dotdigital list" : "Choose an account first");

                return (
                  <Flex key={key} direction="row" justify="between" style={{ width: "100%" }}>
                    <Flex style={{ width: "32%", minWidth: 0 }}>
                      <Flex style={{ width: "100%", backgroundColor: "var(--gray-3)", border: "1px solid var(--gray-a7)", borderRadius: "var(--radius-2)", padding: "0 var(--space-2)", height: "34px", alignItems: "center", overflow: "hidden" }}>
                        <Text size="2" title={loc.name ?? loc.label ?? ""} style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%", color: "var(--gray-a11)" }}>
                          {loc.name ?? loc.label ?? ""}
                        </Text>
                      </Flex>
                    </Flex>

                    <Flex align="center" style={{ paddingLeft: "8px", paddingRight: "8px" }}>
                      <IconButton size="2" variant="ghost" color="gray" style={{ pointerEvents: "none" }}>
                        <ArrowRightIcon width="16" height="16" />
                      </IconButton>
                    </Flex>

                    <Flex style={{ width: "32%", minWidth: 0 }}>
                      <Select.Root value={mapping.dotdigital_config_id || ""} onValueChange={(value) => handleLocationConfigChange(key, value)} disabled={!isOwner}>
                        <Select.Trigger placeholder="Select account..." style={{ width: "100%", boxSizing: "border-box" }}>
                          <div style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%" }} title={selectedConfigLabel}>
                            {selectedConfigLabel}
                          </div>
                        </Select.Trigger>
                        <Select.Content side="bottom" align="start" sideOffset={4}>
                          {dotdigitalConfigs.map((config) => (
                            <Select.Item key={config.id} value={config.id} title={getDotdigitalConfigLabel(config)}>
                              {getDotdigitalConfigLabel(config)}
                            </Select.Item>
                          ))}
                        </Select.Content>
                      </Select.Root>
                    </Flex>

                    <Flex align="center" style={{ paddingLeft: "8px", paddingRight: "8px" }}>
                      <IconButton size="2" variant="ghost" color="gray" style={{ pointerEvents: "none" }}>
                        <ArrowRightIcon width="16" height="16" />
                      </IconButton>
                    </Flex>

                    <Flex style={{ width: "32%", minWidth: 0 }}>
                      <Select.Root value={mapping.dotdigital_list_id || ""} onValueChange={(value) => handleLocationDotdigitalListChange(key, value)} disabled={!isOwner || !mapping.dotdigital_config_id}>
                        <Select.Trigger placeholder="Select list..." style={{ width: "100%", boxSizing: "border-box" }}>
                          <div style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%" }} title={selectedListName}>
                            {selectedListName}
                          </div>
                        </Select.Trigger>
                        <Select.Content side="bottom" align="start" sideOffset={4}>
                          {availableLists.map((list) => <Select.Item key={list.id} value={list.id} title={list.name}>{list.name}</Select.Item>)}
                        </Select.Content>
                      </Select.Root>
                    </Flex>
                  </Flex>
                );
              })
            ) : (
              <Flex justify="center" style={{ padding: "20px 0" }}>
                <Text>No independent locations found.</Text>
              </Flex>
            )}

            <Flex justify="end">
              <Button size="2" variant="solid" color="blue" onClick={() => handleSaveLocationMappings("independent")} loading={isIndependentSaving} disabled={!isOwner || !hasIndependentLocationChanges || hasIndependentHalfFilled || isBrandsSaving || isIndependentSaving}>
                Save changes
              </Button>
            </Flex>
          </Flex>
        </ActiveIntegrateCard.Section>
      </ActiveIntegrateCard.Root>

      <AuthDotdigitalDialog
        isOpen={openAddAccountDialog}
        onOpenChange={handleAddAccountDialogOpenChange}
        onCancel={resetAddAccountState}
        onContinue={handleAddAccounts}
        onAddAccount={validatePendingAccount}
        onRemoveAccount={handleRemovePendingAccount}
        dotdigitalConfigs={pendingDotdigitalConfigs}
        formData={addAccountFormData}
        onFieldChange={handleAddAccountFieldChange}
        onFieldMappingChange={handleAddAccountFieldMappingChange}
        fieldErrors={addAccountFieldErrors}
        emailOptInOptions={DOTDIGITAL_EMAIL_OPT_IN_OPTIONS}
        contactSyncAudienceOptions={DOTDIGITAL_CONTACT_SYNC_AUDIENCE_OPTIONS}
        isAddAccountLoading={isValidatingPendingAccount}
        isContinueLoading={isAddingAccounts}
        isContinueDisabled={pendingDotdigitalConfigs.length === 0}
        apiError={addAccountApiError}
        title="Add Dotdigital Accounts"
        continueLabel="Add accounts"
        showContinueStep={false}
        detailsDescription="Queue each account you want to add, then save them back into this integration."
        queueDescription="These validated accounts will be added to your connected account list."
        showDataFieldMappings
      />

      <Dialog.Root open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <Dialog.Content maxWidth="460px">
          <Dialog.Title>Disconnect Dotdigital</Dialog.Title>
          <Dialog.Description size="2" mb="4">
            This will remove all Dotdigital configuration, location mappings, and any stored logs for your organisation. Please contact b-connect support if you have any questions or concerns.
          </Dialog.Description>

          <Flex justify="end" gap="3">
            <Button variant="soft" color="gray" onClick={() => setShowConfirmDialog(false)}>
              Cancel
            </Button>
            <Button variant="soft" color="red" onClick={handleDisconnect} loading={isDisconnecting}>
              Disconnect
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      <PermissionAlertDialog
        isOpen={showPermissionAlert}
        onOpenChange={setShowPermissionAlert}
        message="Only owners can perform this action"
      />

      <div style={{ position: "fixed", zIndex: 2147483647, pointerEvents: "none" }}>
        <ToastAlert />
      </div>
    </>
  );
}
